<template>
  <div class="space-y-2">
    <!-- Added locations as removable chips: the name, nothing more. -->
    <div v-if="modelValue.length" class="flex flex-wrap gap-2">
      <Chip
        v-for="(item, i) in modelValue"
        :key="`${item.name}-${i}`"
        data-test="wizard-location-chip"
        :remove-label="t('onboarding.actions.remove')"
        @remove="removeAt(i)"
      >
        <span class="truncate max-w-[16rem]">{{ item.name }}</span>
      </Chip>
    </div>
    <p v-else-if="emptyText" class="text-sm text-tertiary">{{ emptyText }}</p>

    <!-- ONE field (#546): what is typed is the location's name, and the same
         field searches towns as you type, as an aid to the name. Picking a town adds
         it under its own name straight away; Enter or ➕ adds the typed text. -->
    <div class="space-y-2">
      <div class="flex gap-2">
        <PlacePicker
          ref="picker"
          v-model:text="draftName"
          :model-value="null"
          :api="peopleApi"
          text-is-name
          class="flex-1 min-w-0"
          data-test="wizard-location-input"
          :placeholder="placeholder"
          @update:model-value="onPicked"
          @keydown.enter="onEnter"
        />
        <Button
          data-test="wizard-location-add"
          variant="secondary"
          :disabled="!draftName.trim()"
          :aria-label="t('onboarding.actions.add')"
          @click="addTyped"
        >
          <LumiIcon name="add" class="h-4 w-4" />
        </Button>
      </div>
      <p class="text-xs text-secondary">{{ t('onboarding.wizard.general.place.hint') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Chip, LumiIcon } from '@lumi/ui';
import { PlacePicker } from '@lumi/app-kit';
import { peopleApi } from '../../utils/api';
import type { StagedLocation } from '../../utils/places';

// The wizard's location list. The sibling of WizardListInput.vue for the one unit
// type with a town search beside its name: what is typed is the location's name,
// and the search is only a way to type a town's name correctly. Nothing about a
// picked town is kept — the location's address, and with it the map point job
// boards need, is entered in People (issue 1065). Same chips, same staged-only
// contract (ADR 0032).
const props = defineProps<{ modelValue: StagedLocation[]; placeholder?: string; emptyText?: string }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: StagedLocation[]): void }>();

const { t } = useI18n();

const draftName = ref('');
const picker = ref<{ close: () => void } | null>(null);

function append(name: string) {
  const trimmed = name.trim();
  // Case-insensitive de-dupe on the NAME (org units are unique by name in
  // People): adding an existing entry just clears the row.
  if (trimmed && !props.modelValue.some((v) => v.name.toLowerCase() === trimmed.toLowerCase())) {
    emit('update:modelValue', [...props.modelValue, { name: trimmed }]);
  }
  draftName.value = '';
}

// The shared picker hands a pick up as its model and clears nothing itself: the
// model is held at null here, so it never shows a selection that still needs
// confirming. A null (the field was edited) is the typed text's business, not a pick.
function onPicked(place: { placeName: string } | null) {
  if (place) append(place.placeName);
}

function addTyped() {
  append(draftName.value);
  picker.value?.close();
}

// Enter that picked a highlighted row was already handled (and swallowed) by the
// combobox; any other Enter takes the typed text.
function onEnter(event: KeyboardEvent) {
  if (event.defaultPrevented || event.isComposing) return;
  addTyped();
}

// Leaving the section keeps what was typed but never added — the ➕ is easy to
// miss, and silently dropping the entry is what made "Weiter" look broken (#546).
function flush() {
  if (draftName.value.trim()) addTyped();
}

function removeAt(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

defineExpose({ flush });
</script>
